import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import {
  ArrowUp,
  ChevronRight,
  Copy,
  Download,
  File as FileIcon,
  Folder,
  FolderPlus,
  Home,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";
import type { SftpFileEntry } from "@lumika/shared";
import {
  Button,
  EmptyState,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@lumika/ui";
import { sftpApi } from "../api/sftp";

const formatSize = (bytes: number | null): string => {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
};

/**
 * Top-level SFTP file browser — single-pane Finder-like view.
 *
 * Per the spec ("regular finder, where admins are able to
 * open/navigate through folders and upload/download/delete files"):
 * one column. Breadcrumbs at the top let you jump back to any
 * ancestor; the toolbar carries Upload / New folder / Go up /
 * Refresh; each row carries per-entry Copy path / Download (files
 * only) / Delete. Double-click a folder (or single-click the
 * folder name link) navigates into it.
 *
 * URL state: `?path=<key>` keeps the current folder so deep-links
 * and back/forward navigation round-trip exactly. Empty path = bucket
 * root.
 */
export function SftpBrowserPage() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Single source of truth: the URL search-param `?path=`.
  const path = params.get("path") ?? "";
  const setPath = (next: string) => {
    const updated = new URLSearchParams(params);
    if (next) updated.set("path", next);
    else updated.delete("path");
    setParams(updated, { replace: true });
  };

  const listQuery = useQuery({
    queryKey: ["admin", "sftp-files-global", path],
    queryFn: () => sftpApi.listGlobal(path || undefined),
    placeholderData: (prev) => prev,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const absPath = `${path}${file.name}`;
      const { url, headers } = await sftpApi.createUploadUrlGlobal({
        path: absPath,
        contentType: file.type || "application/octet-stream",
      });
      const res = await fetch(url, { method: "PUT", headers, body: file });
      if (!res.ok)
        throw new Error(`Upload failed: ${res.status} ${res.statusText}`);
      return absPath;
    },
    onSuccess: (uploaded) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "sftp-files-global"],
      });
      toast.success(`Uploaded ${uploaded}`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (absPath: string) => sftpApi.deletePathGlobal(absPath),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "sftp-files-global"],
      });
      toast.success("Deleted.");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const newFolderMutation = useMutation({
    mutationFn: (absPath: string) => sftpApi.createFolderGlobal(absPath),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "sftp-files-global"],
      });
      toast.success("Folder created.");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const entries = listQuery.data?.entries ?? [];

  const navigateInto = (entry: SftpFileEntry) => {
    if (entry.type !== "directory") return;
    setPath(entry.path.endsWith("/") ? entry.path : `${entry.path}/`);
  };

  const navigateUp = () => {
    if (!path) return;
    const trimmed = path.endsWith("/") ? path.slice(0, -1) : path;
    const lastSlash = trimmed.lastIndexOf("/");
    setPath(lastSlash >= 0 ? `${trimmed.slice(0, lastSlash)}/` : "");
  };

  const handleDownload = async (absPath: string) => {
    try {
      const { url } = await sftpApi.createDownloadUrlGlobal(absPath);
      // window.open after an await is outside the user-gesture tick, so
      // strict popup blockers (Safari) can return null — surface it.
      const win = window.open(url, "_blank", "noopener");
      if (!win) toast.error("Download blocked by the browser — allow popups for this site.");
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const copyAbsolutePath = async (absPath: string) => {
    try {
      await navigator.clipboard.writeText(absPath);
      toast.success(`Copied ${absPath}`);
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  };

  const handleNewFolder = () => {
    const name = prompt("New folder name (relative to current path)");
    if (name === null) return;
    const r = z
      .string()
      .trim()
      .min(1)
      .max(128)
      .regex(/^[^/\\]+$/, "No slashes allowed.")
      .safeParse(name.trim());
    if (!r.success) {
      toast.error(r.error.issues[0]?.message ?? "Invalid folder name.");
      return;
    }
    newFolderMutation.mutate(`${path}${r.data}/`);
  };

  if (listQuery.isLoading && !listQuery.data)
    return <PageLoading message="Loading SFTP files…" />;
  if (listQuery.error)
    return <PageError description={(listQuery.error as Error).message} />;

  const segments = path.replace(/\/$/, "").split("/").filter(Boolean);

  return (
    <div className="space-y-6">
      <PageHeader
        title="SFTP file browser"
        description="Browse, upload, download, and delete files anywhere on the SFTP bucket. Navigate folders by clicking; copy the absolute path of any entry to paste into an Inventory Import."
      />

      <Section
        title={
          <div className="flex flex-wrap items-center gap-1 text-xs">
            <button
              type="button"
              className="flex items-center gap-1 rounded px-2 py-1 hover:bg-muted"
              onClick={() => setPath("")}
              title="Bucket root"
            >
              <Home className="size-3" />
              <span className="font-mono">bucket</span>
            </button>
            {segments.map((segment, i) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight className="size-3 text-muted-foreground" />
                <button
                  type="button"
                  className="rounded px-1.5 py-0.5 font-mono hover:underline"
                  onClick={() => setPath(segments.slice(0, i + 1).join("/") + "/")}
                >
                  {segment}
                </button>
              </span>
            ))}
          </div>
        }
      >
        <div className="mb-3 flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (
                file &&
                (!entries.some((en) => en.path === `${path}${file.name}`) ||
                  confirm(`${file.name} already exists here. Replace it?`))
              ) {
                uploadMutation.mutate(file);
              }
              e.target.value = "";
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={navigateUp}
            disabled={!path}
            title={path ? "Go to parent folder" : "Already at bucket root"}
          >
            <ArrowUp className="mr-1 size-4" /> Up
          </Button>
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
          >
            <Upload className="mr-1 size-4" /> Upload
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleNewFolder}
            disabled={newFolderMutation.isPending}
          >
            <FolderPlus className="mr-1 size-4" /> New folder
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              queryClient.invalidateQueries({
                queryKey: ["admin", "sftp-files-global"],
              })
            }
          >
            <RefreshCw
              className={
                listQuery.isFetching ? "mr-1 size-4 animate-spin" : "mr-1 size-4"
              }
            />{" "}
            Refresh
          </Button>
        </div>

        {entries.length === 0 && !path ? (
          <EmptyState
            icon={<Folder className="size-10" />}
            title="Empty SFTP bucket"
            description="No reserved folders yet. Create an SFTP user on the credentials page to lay one down."
          />
        ) : entries.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Empty folder.
          </p>
        ) : (
          // While a new folder loads, the previous listing stays rendered
          // (placeholderData) — dim it so the stale entries can't be
          // mistaken for the folder named in the breadcrumb.
          <div
            className={
              listQuery.isFetching ? "pointer-events-none opacity-50" : undefined
            }
          >
            {/* Desktop: Finder-like data table. */}
            <TableBleed className="-mb-6 hidden border-t overflow-hidden rounded-b-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/2">Name</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Modified</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => {
                    const isDir = e.type === "directory";
                    const name = e.path.replace(/\/$/, "").split("/").pop();
                    return (
                      <TableRow
                        key={e.path}
                        className={isDir ? "cursor-pointer hover:bg-muted/50" : "hover:bg-muted/30"}
                        onDoubleClick={() => isDir && navigateInto(e)}
                      >
                        <TableCell>
                          {isDir ? (
                            <button
                              type="button"
                              className="flex w-full cursor-pointer items-center gap-2 font-mono text-sm hover:underline"
                              onClick={() => navigateInto(e)}
                            >
                              <Folder className="size-4 text-amber-500" />
                              {name}/
                            </button>
                          ) : (
                            // Files aren't clickable — a plain span avoids a
                            // focusable no-op control.
                            <span className="flex w-full items-center gap-2 font-mono text-sm">
                              <FileIcon className="size-4 text-muted-foreground" />
                              {name}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{formatSize(e.size)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {e.lastModified
                            ? new Date(e.lastModified).toLocaleString()
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                copyAbsolutePath(e.path);
                              }}
                              title="Copy path"
                            >
                              <Copy className="size-4" />
                            </Button>
                            {e.type === "file" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  handleDownload(e.path);
                                }}
                                title="Download"
                              >
                                <Download className="size-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                if (
                                  confirm(
                                    `Delete ${isDir ? "folder" : "file"} ${e.path}?`,
                                  )
                                ) {
                                  deleteMutation.mutate(e.path);
                                }
                              }}
                              title="Delete"
                            >
                              <Trash2 className="size-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableBleed>
            {/* Mobile: condensed touch list over the same entries. */}
            <MobileList>
              {entries.map((e) => {
                const isDir = e.type === "directory";
                const name = e.path.replace(/\/$/, "").split("/").pop();
                return (
                  <MobileListRow
                    key={e.path}
                    onClick={isDir ? () => navigateInto(e) : undefined}
                    primary={
                      <span className="flex items-center gap-2 font-mono">
                        {isDir ? (
                          <Folder className="size-4 shrink-0 text-amber-500" />
                        ) : (
                          <FileIcon className="size-4 shrink-0 text-muted-foreground" />
                        )}
                        <span className="truncate">
                          {name}
                          {isDir && "/"}
                        </span>
                      </span>
                    }
                    secondary={
                      <>
                        {formatSize(e.size)}
                        {" · "}
                        {e.lastModified
                          ? new Date(e.lastModified).toLocaleString()
                          : "—"}
                      </>
                    }
                    trailing={
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            copyAbsolutePath(e.path);
                          }}
                          title="Copy path"
                        >
                          <Copy className="size-4" />
                        </Button>
                        {e.type === "file" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              handleDownload(e.path);
                            }}
                            title="Download"
                          >
                            <Download className="size-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            if (
                              confirm(
                                `Delete ${isDir ? "folder" : "file"} ${e.path}?`,
                              )
                            ) {
                              deleteMutation.mutate(e.path);
                            }
                          }}
                          title="Delete"
                        >
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </div>
                    }
                  />
                );
              })}
            </MobileList>
          </div>
        )}
      </Section>
    </div>
  );
}
